import { Info } from 'components/icons';
import { RecordChip } from 'components/record-chip/RecordChip';
import { Checkbox } from 'components/ui/checkbox';
import InfoTooltip from 'components/InfoTooltip.tsx';
import Loader from 'components/Loader.tsx';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import BasicTable from 'components/tables/BasicTable.tsx';
import type { DataTableColumnDef } from 'components/tables/data-table';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import { InvoiceNumber } from 'pages/invoices/InvoiceTableCells.tsx';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useGetInvoicesWithFiltersQuery } from 'store/api/invoices.ts';
import { InvoiceData, InvoiceDataListItem } from 'types/invoices.ts';
import { t } from 'utils/getTranslations';
import ConfirmationModal from '../confirmation/ConfirmationModal.tsx';
import { ModalFooter } from '../ModalFooter.tsx';
import { ModalHeader } from '../ModalHeader.tsx';
import { useModal } from '../useModal.ts';

interface Props {
    invoice: InvoiceData;
    onClose: () => void;
    onSubmit: (value: number[]) => void;
}

const MergeInvoiceModal = (props: Props) => {
    const { invoice, onClose, onSubmit } = props;
    const { openModal, closeModal } = useModal();
    const isNew = useIsNewDesign();
    const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
    const [selectedMatters, setSelectedMatters] = useState<number[]>(
        invoice.invoiceMatters?.map((im) => im.matterId) || []
    );

    const { data: mergableInvoices, isLoading } = useGetInvoicesWithFiltersQuery({
        pageSize: 2147483647,
        page: 0,
        filters: `id=mergematch=${invoice.id}`,
    });

    const noRecordsElement = (
        <p className="px-4 py-2 text-xs leading-5">
            <FormattedMessage id="invoices.no-invoices-found" />
        </p>
    );

    const handleCheckboxChange = useCallback(
        (invoiceId: number, matterIds: number[], checked: boolean) => {
            const newSet = new Set(selectedInvoices);
            let newSelectedMatters: number[] = [];

            if (checked) {
                newSet.add(invoiceId);
                newSelectedMatters = [...selectedMatters, ...matterIds];
            } else {
                newSet.delete(invoiceId);
                newSelectedMatters = selectedMatters.filter((matterId) => !matterIds.includes(matterId));
            }

            setSelectedInvoices(Array.from(newSet));
            setSelectedMatters(newSelectedMatters);
        },
        [selectedInvoices, selectedMatters]
    );

    const hasDuplicateMatter = useMemo(() => {
        return selectedMatters.length !== new Set(selectedMatters).size;
    }, [selectedMatters]);

    const columns = useMemo<DataTableColumnDef<InvoiceDataListItem>[]>(() => {
        // Why an unticked row cannot be ticked: the case it shares with this invoice or with a ticked one.
        const clashOf = ({ id, matters }: InvoiceDataListItem) => {
            if (selectedInvoices.includes(id)) {
                return null;
            }
            const shared = (matters || []).find((matter) => selectedMatters.includes(matter.id));
            if (!shared) {
                return null;
            }
            const tickedWithCase = (mergableInvoices?.content || []).find(
                (other) => selectedInvoices.includes(other.id) && (other.matters || []).some((m) => m.id === shared.id)
            );
            return t('invoices.merge-modal.cannot-select-reason', {
                case: shared.caseNumber,
                invoice: tickedWithCase?.invoiceNumber || 'none',
            });
        };
        const reasonId = (id: number) => `merge-cannot-select-${id}`;
        return [
            {
                id: 'id',
                accessorKey: 'id',
                // Explicit empty header: the selection column has no label, and a column that leaves
                // `header` out falls back to rendering its own accessor key.
                header: '',
                cell: ({ row }) => {
                    const { id, matters } = row.original;
                    const matterIds = (matters || []).map((m) => m.id);
                    const isChecked = selectedInvoices.includes(id);
                    const isDisabled = clashOf(row.original) !== null;

                    return (
                        <div className="flex items-center">
                            <Checkbox
                                variant="secondary"
                                checked={isChecked}
                                onCheckedChange={(checked) => handleCheckboxChange(id, matterIds, checked)}
                                disabled={isDisabled}
                                aria-label={
                                    isNew
                                        ? t('invoices.merge-modal.select-invoice', {
                                              number: row.original.invoiceNumber ?? '',
                                          })
                                        : undefined
                                }
                                aria-describedby={isNew && isDisabled ? reasonId(id) : undefined}
                            />
                            {/* The new design says why in the row itself. */}
                            {isDisabled && !isNew && (
                                <InfoTooltip text="invoices.merge-modal.cannot-select-tooltip" className="ml-1" />
                            )}
                        </div>
                    );
                },
                meta: {
                    width: '10%',
                },
            },
            {
                header: t('invoices.invoice-number'),
                id: 'invoiceNumber',
                accessorKey: 'invoiceNumber',
                cell: ({ row }) => {
                    const reason = isNew ? clashOf(row.original) : null;
                    return (
                        <>
                            {isNew ? (
                                <RecordChip
                                    entityType="INVOICE"
                                    id={row.original.id}
                                    label={row.original.invoiceNumber ?? ''}
                                    hitArea={false}
                                />
                            ) : (
                                <InvoiceNumber invoice={row.original} />
                            )}
                            {reason && (
                                <span
                                    id={reasonId(row.original.id)}
                                    className="text-muted-foreground mt-0.5 flex items-center gap-1 text-xs whitespace-nowrap"
                                >
                                    <Info aria-hidden className="size-3 shrink-0" />
                                    {reason}
                                </span>
                            )}
                        </>
                    );
                },
                meta: {
                    width: 0,
                },
            },
            {
                header: t('date'),
                id: 'invoiceDate',
                accessorKey: 'invoiceDate',
                cell: ({ getValue }) => {
                    const invoiceDate = getValue<InvoiceDataListItem['invoiceDate']>();
                    return invoiceDate && <DateDefaultNowrap dateValue={invoiceDate} />;
                },
                meta: {
                    width: 0,
                },
            },
            {
                header: t('invoices.gross-total'),
                id: 'grossTotal',
                cell: ({ row }) => {
                    const { paymentCurrencyTypeCode, totalAmountInPaymentCurrency } = row.original;
                    return <MoneyDisplay amount={totalAmountInPaymentCurrency} currency={paymentCurrencyTypeCode} />;
                },
                meta: {
                    width: 0,
                    className: isNew ? 'text-end' : undefined,
                },
            },
        ];
    }, [selectedInvoices, selectedMatters, handleCheckboxChange, mergableInvoices, isNew]);

    const handleOnSave = useCallback(() => {
        if (selectedInvoices.length) {
            openModal(
                <ConfirmationModal
                    onCancel={closeModal}
                    onConfirm={() => {
                        onSubmit(selectedInvoices);
                        closeModal();
                        onClose();
                    }}
                    content={<p className="text-body1">{t('invoices.merge-modal.merge-confirmation-text')}</p>}
                    cancelId="no"
                    alert={{
                        titleId: 'invoices.confirm.merge-title',
                        titleValues: { count: selectedInvoices.length, invoice: invoice.invoiceNumber || 'none' },
                        description: t('invoices.merge-modal.merge-confirmation-text'),
                        confirmId: 'invoices.merge',
                    }}
                />
            );
        }
    }, [selectedInvoices, invoice.invoiceNumber, onClose, onSubmit, openModal, closeModal]);

    return (
        <>
            <ModalHeader
                kit
                title={t('invoices.merge-modal.title', { number: invoice.invoiceNumber })}
                description={t('invoices.merge-modal.description')}
                onCancel={onClose}
            />
            {/* The new design's dialog fits its rows; the table scrolls inside its own bound. */}
            <div className="new:h-auto h-[800px] overflow-y-auto p-6 max-sm:px-4">
                {isLoading ? (
                    <Loader />
                ) : (
                    <BasicTable
                        columns={columns}
                        data={mergableInvoices?.content || []}
                        stickyHeader
                        containerClassName="max-h-[600px]"
                        noContentJSX={noRecordsElement}
                        getRowClassName={(row) =>
                            isNew && selectedInvoices.includes(row.original.id) ? 'bg-muted' : undefined
                        }
                    />
                )}
            </div>
            <ModalFooter
                kit
                saveButtonTextId="invoices.merge"
                extraButton={
                    isNew && selectedInvoices.length > 0 ? (
                        <span className="text-muted-foreground text-sm">
                            <FormattedMessage
                                id="invoices.merge-modal.selected-count"
                                values={{ count: selectedInvoices.length }}
                            />
                        </span>
                    ) : undefined
                }
                disableSave={hasDuplicateMatter}
                disabledSaveInfoTextId="invoices.merge-modal.disabled-save-info"
                onCancel={onClose}
                onSave={handleOnSave}
            />
        </>
    );
};

export default MergeInvoiceModal;
